// Message rendering, markdown, debug logging, and chat UI helpers.

import { showDebugLogs, documentReady, lastScan, setLastScan, setShowDebugLogs } from "./state";
import { scanDocument, buildDelta, storeNodeReference } from "../core/documentScanner";
import { marked } from "marked";
import DOMPurify from "dompurify";

export function _timeStr() {
  const d = new Date();
  const h = d.getHours();
  const m = String(d.getMinutes()).padStart(2, "0");
  const ampm = h >= 12 ? "PM" : "AM";
  return `${h % 12 || 12}:${m} ${ampm}`;
}

export const _labels = { user: "You", ai: "JEWEL", system: "System", dev: "Dev" };

export function setUserDisplayName(name) {
  _labels.user = name && name.trim() ? name.trim().split(" ")[0] : "You";
}

// Show/hide empty chat placeholder (visible when ready + no user/ai messages)
export function updateEmptyChat() {
  const el = document.getElementById("chat-empty");
  if (!el) return;

  if (!documentReady) {
    el.style.display = "none";
    return;
  }

  const chatEl = document.getElementById("chat-area");
  const hasVisibleContent = chatEl.querySelectorAll(".msg.user, .msg.ai").length > 0;
  el.style.display = hasVisibleContent ? "none" : "";
}

// Dev-only log — renders backend data as hidden system messages (full, collapsible)
// export function devLog(tag, data) {
//   const json = typeof data === "string" ? data : JSON.stringify(data, null, 2);
//   const wrapper = addMessage("system", "");
//   if (!wrapper) return;
//   const body = wrapper.querySelector(".msg-body");
//   if (!body) return;
//   if (json.length > 600) {
//     const details = document.createElement("details");
//     const summary = document.createElement("summary");
//     summary.textContent = `[${tag}] (${json.length} chars — click to expand)`;
//     summary.style.cursor = "pointer";
//     const pre = document.createElement("pre");
//     pre.style.cssText = "white-space:pre-wrap;word-break:break-all;font-size:10px;max-height:400px;overflow:auto;margin:4px 0 0;";
//     pre.textContent = json;
//     details.appendChild(summary);
//     details.appendChild(pre);
//     body.appendChild(details);
//   } else {
//     body.textContent = `[${tag}] ${json}`;
//   }
// }
export function devLog(_tag, _data) {} // no-op

// Run scanner and dump full output as JSON block in chat (debug only)
export async function debugScan() {
  try {
    let scan;
    let scanMs;
    if (lastScan) {
      scan = lastScan;
      scanMs = "0 (cached)";
    } else {
      const scanStart = performance.now();
      scan = await scanDocument();
      setLastScan(scan);
      storeNodeReference(scan.nodes);
      scanMs = (performance.now() - scanStart).toFixed(0);
    }

    const delta = buildDelta(scan);
    const output = { scanTimeMs: Number(scanMs), delta, scan };
    const json = JSON.stringify(output, null, 2);

    const chatEl = document.getElementById("chat-area");
    const wrapper = document.createElement("div");
    wrapper.className = "msg system";
    wrapper.style.display = showDebugLogs ? "" : "none";

    const label = document.createElement("div");
    label.className = "msg-label";
    label.textContent = "SCAN_OUTPUT";

    const copyBtn = document.createElement("button");
    copyBtn.textContent = "📋 Copy JSON";
    copyBtn.style.cssText =
      "font-size:11px;padding:3px 8px;margin:4px 0;border:1px solid #555;" +
      "border-radius:4px;background:#2d2d2d;color:#ccc;cursor:pointer;";
    copyBtn.addEventListener("click", () => {
      navigator.clipboard.writeText(json).then(() => {
        copyBtn.textContent = "✅ Copied!";
        setTimeout(() => {
          copyBtn.textContent = "📋 Copy JSON";
        }, 1500);
      });
    });

    const pre = document.createElement("pre");
    pre.style.cssText =
      "white-space:pre-wrap;word-break:break-all;font-size:11px;" +
      "max-height:400px;overflow:auto;background:#1e1e1e;color:#d4d4d4;" +
      "padding:8px;border-radius:6px;margin:4px 0;line-height:1.4;";
    pre.textContent = json;

    wrapper.appendChild(label);
    wrapper.appendChild(copyBtn);
    wrapper.appendChild(pre);
    chatEl.appendChild(wrapper);
    chatEl.scrollTop = chatEl.scrollHeight;
  } catch (err) {
    devLog("SCAN_ERR", `${err.name}: ${err.message}\n${err.stack || ""}`);
  }
}

// export function toggleDebugLogs() {
//   setShowDebugLogs(!showDebugLogs);
//   const btn = document.getElementById("debug-toggle");
//   btn.classList.toggle("active", showDebugLogs);
//   btn.title = showDebugLogs ? "Hide system logs" : "Show system logs";
//   document.querySelectorAll(".msg.system").forEach((el) => {
//     el.style.display = showDebugLogs ? "" : "none";
//   });
//   updateEmptyChat();
// }
export function toggleDebugLogs() {} // no-op

// Add a message bubble to chat area
export function addMessage(type, text) {
  const chatEl = document.getElementById("chat-area");

  const wrapper = document.createElement("div");
  wrapper.className = `msg ${type}`;

  if (type === "system" && !showDebugLogs) {
    wrapper.style.display = "none";
  }

  const label = document.createElement("div");
  label.className = "msg-label";
  label.textContent = _labels[type] || type;

  const body = document.createElement("div");
  body.className = "msg-body";

  if (type === "ai") {
    body.innerHTML = renderMarkdown(text);
  } else {
    body.textContent = text;
  }

  const time = document.createElement("div");
  time.className = "msg-time";
  time.textContent = _timeStr();

  wrapper.appendChild(label);
  wrapper.appendChild(body);

  if (type === "ai" || type === "user") {
    const footer = document.createElement("div");
    footer.className = "msg-footer-row";
    footer.appendChild(time);
    wrapper.appendChild(footer);
  } else {
    wrapper.appendChild(time);
  }

  chatEl.appendChild(wrapper);
  chatEl.scrollTop = chatEl.scrollHeight;

  updateEmptyChat();

  return wrapper;
}

// Render markdown to HTML with table scroll wrappers
export function renderMarkdown(text) {
  if (!text) return "";
  try {
    let html = marked.parse(text);
    html = html.replace(/<table>/g, '<div class="table-scroll"><table>');
    html = html.replace(/<\/table>/g, "</table></div>");
    // Wrap "**Recommendation:**" paragraphs in amber recommendation block
    html = html.replace(
      /<p><strong>Recommendation:<\/strong>([\s\S]*?)<\/p>/gi,
      '<div class="recommendation-block"><p><strong>Recommendation:</strong>$1</p></div>'
    );
    return DOMPurify.sanitize(html, { USE_PROFILES: { html: true } });
  } catch {
    return DOMPurify.sanitize(text);
  }
}

// Strip suggested_edit blocks and internal paragraph aliases (p1, p2, etc.)
export function _cleanAIResponse(text) {
  if (!text) return "";
  let clean = text.replace(/```suggested_edit\s*\n?[\s\S]*?\n?```/gi, "").trim();
  clean = clean.replace(/\s*\((?:for\s+)?p\d+(?:\s*,\s*p\d+)*(?:\s*(?:,\s*)?and\s+p\d+)?\)/gi, "");
  clean = clean.replace(/\bfor\s+p\d+(?:\s*,\s*p\d+)*(?:\s+and\s+p\d+)?\b/gi, "");
  clean = clean.replace(/\[p\d+\]/g, "the selected paragraph");
  clean = clean.replace(/\bp\d+(?:\s*(?:,\s*|\s+and\s+)\s*p\d+)+\b/gi, "the selected paragraphs");
  clean = clean.replace(/\*\*p\d+:?\*\*/g, "the selected paragraph");
  clean = clean.replace(/(?<=^|[\s(,])p(\d{1,2})(?=[:;,.\s)\n]|$)/gim, "the selected paragraph");
  clean = clean.replace(/  +/g, " ").trim();
  return clean;
}

const _COPY_ICON = '<svg width="13" height="13" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="5" y="5" width="9" height="9" rx="1.5" stroke="currentColor" stroke-width="1.4"/><path d="M11 3H3.5A1.5 1.5 0 0 0 2 4.5V12" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>';
const _CHECK_ICON = '<svg width="13" height="13" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M3 8.5l3 3 7-7" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';

// Strip raw AI response to plain copyable text:
// - removes suggested_edit fenced blocks and all code blocks
// - converts literal \n sequences to real newlines
// - strips markdown bold/italic/heading markers
// - removes internal paragraph aliases (p1, p2, ...)
function _plainTextForCopy(rawText) {
  if (!rawText) return "";
  let t = rawText;
  // Remove suggested_edit fenced blocks
  t = t.replace(/```suggested_edit\s*\n?[\s\S]*?\n?```/gi, "");
  // Remove all other code/json fenced blocks
  t = t.replace(/```[a-z]*\s*\n?[\s\S]*?\n?```/g, "");
  // Convert literal \n (two-char sequence) to real newline
  t = t.replace(/\\n/g, "\n");
  // Strip markdown bold/italic markers (keep the inner text)
  t = t.replace(/\*{1,3}([^*\n]+)\*{1,3}/g, "$1");
  t = t.replace(/_{1,2}([^_\n]+)_{1,2}/g, "$1");
  // Strip markdown headings
  t = t.replace(/^#{1,6}\s+/gm, "");
  // Strip inline code backticks (keep text)
  t = t.replace(/`([^`]+)`/g, "$1");
  // Remove internal paragraph aliases
  t = t.replace(/\s*\((?:for\s+)?p\d+(?:\s*,\s*p\d+)*(?:\s*(?:,\s*)?and\s+p\d+)?\)/gi, "");
  t = t.replace(/\bfor\s+p\d+(?:\s*,\s*p\d+)*(?:\s+and\s+p\d+)?\b/gi, "");
  t = t.replace(/\[p\d+\]/g, "");
  // Collapse 3+ blank lines to 2
  t = t.replace(/\n{3,}/g, "\n\n");
  return t.trim();
}

// Create a copy-response button for an AI message (icon-only)
export function _makeCopyBtn(rawText) {
  const cleanText = _plainTextForCopy(rawText);
  const btn = document.createElement("button");
  btn.className = "copy-response-btn";
  btn.setAttribute("data-tooltip", "Copy");
  btn.innerHTML = _COPY_ICON;
  btn.addEventListener("click", async () => {
    try {
      await navigator.clipboard.writeText(cleanText);
      btn.innerHTML = _CHECK_ICON;
      setTimeout(() => { btn.innerHTML = _COPY_ICON; }, 1500);
    } catch {
      setTimeout(() => { btn.innerHTML = _COPY_ICON; }, 1500);
    }
  });
  return btn;
}
